import { useCallback, useRef, useState } from "react";
import { BsLinkedin } from "react-icons/bs";
import { Check, ChevronDown } from "lucide-react";
import { FloatingMenu, MENU_ITEM } from "../ui/FloatingMenu";
import { LINKEDIN_STAGE_LABEL, LinkedinStage } from "../../lib/linkedinCrm";
import { setStageFromCrm } from "../../interfaces/linkedinProspecting";

const STAGES = Object.keys(LINKEDIN_STAGE_LABEL) as LinkedinStage[];

/**
 * A CRM card's stage on the LinkedIn page, shown next to its LinkedIn link and changed from there:
 * the LinkedIn page and its rates read the same record (matched on the profile URL).
 */
export function LinkedinStageMenu({
    prospectId, stage, onChange,
}: { prospectId: string; stage: LinkedinStage; onChange: (stage: LinkedinStage, followUpAt: string | null) => void }) {
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(false);
    const trigger = useRef<HTMLButtonElement>(null);
    const close = useCallback(() => setOpen(false), []);

    const pick = async (next: LinkedinStage) => {
        close();
        if (next === stage) return;
        setBusy(true);
        setError(false);
        try {
            const res = await setStageFromCrm(prospectId, next);
            onChange(res.stage, res.followUpAt);
        } catch {
            setError(true);
        } finally {
            setBusy(false);
        }
    };

    return (
        <span onClick={(e) => e.stopPropagation()}>
            <button
                ref={trigger}
                type="button"
                disabled={busy}
                onClick={() => setOpen((o) => !o)}
                aria-haspopup="menu"
                aria-expanded={open}
                title={error ? "Le statut n'a pas pu être enregistré" : "Statut sur la page LinkedIn"}
                className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 font-sans text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent disabled:opacity-40 ${
                    error ? "border-danger/30 text-danger" : "border-border text-ink-muted hover:border-border-strong hover:text-ink"
                }`}
            >
                <BsLinkedin className="h-3 w-3" />
                {LINKEDIN_STAGE_LABEL[stage]}
                <ChevronDown className="h-3 w-3" />
            </button>
            <FloatingMenu anchor={trigger} open={open} onClose={close} align="left" width={208}>
                {STAGES.map((s) => (
                    <button key={s} role="menuitemradio" aria-checked={s === stage} onClick={() => pick(s)} className={MENU_ITEM}>
                        <span className="flex h-4 w-4 shrink-0 items-center justify-center">{s === stage && <Check className="h-4 w-4" />}</span>
                        {LINKEDIN_STAGE_LABEL[s]}
                    </button>
                ))}
            </FloatingMenu>
        </span>
    );
}
